import React, { memo } from "react";
import { Text, View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { LineChart } from "react-native-gifted-charts";
import Icons from "@/assets/icons";
import { router } from "expo-router";
import { UserFinanceDataResponse } from "@/services/userFinanceDataServices";
import { Colors } from "@/constants/Colors";
import { SCREEN_WIDTH } from "@/constants/Sizes";

const CARD_MARGIN = 16; // 16px margin on each side
const CARD_PADDING = 24; // 24px padding on each side of card
const POINTER_LABEL_WIDTH = 80; // Width for the pointer label
const CHART_WIDTH = SCREEN_WIDTH - CARD_MARGIN - CARD_PADDING - 32; // Extra buffer for pointer label
const MAX_SPACING = 30; // Maximum pixels between chart points before centering

interface SpendChartProps {
  financeData: UserFinanceDataResponse | null;
}

// Helper to format currency from cents
const formatCurrency = (cents: number): string => {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Helper to format date range (last 30 days)
const formatDateRange = (): string => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
  const startStr = thirtyDaysAgo.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endStr = now.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${startStr} - ${endStr}`;
};

// Generate chart data from actual daily spending in netWorth.byDay (last 30 days)
const formatPointerDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const generateChartData = (
  financeData: UserFinanceDataResponse | null,
): { value: number; date: string }[] | null => {
  if (!financeData?.analysis?.netWorth?.byDay?.length) {
    return null;
  }

  const { byDay } = financeData.analysis.netWorth;

  // Take only the last 30 days
  const last30Days = byDay.slice(-30);

  // Use actual spending from each day (convert cents to dollars for chart)
  return last30Days.map((day) => ({
    value: day.spending / 100,
    date: day.date,
  }));
};

// Get total spending for last 30 days
const calculateSpend = (
  financeData: UserFinanceDataResponse | null,
): number | null => {
  if (!financeData?.analysis?.netWorth?.byDay?.length) {
    return null;
  }
  const { byDay } = financeData.analysis.netWorth;
  // Sum spending from the last 30 days
  const last30Days = byDay.slice(-30);
  return last30Days.reduce((sum, day) => sum + day.spending, 0);
};

// Calculate trend percentage (last 30 days vs previous 30 days)
const calculateTrend = (
  financeData: UserFinanceDataResponse | null,
): number | null => {
  if (!financeData?.analysis?.netWorth?.byDay?.length) {
    return null;
  }

  const { byDay } = financeData.analysis.netWorth;

  if (byDay.length < 60) {
    return 0; // Need at least 60 days for comparison
  }

  // Last 30 days spending
  const last30Days = byDay.slice(-30);
  const currentSpending = last30Days.reduce((sum, d) => sum + d.spending, 0);

  // Previous 30 days spending (days 31-60 from end)
  const previous30Days = byDay.slice(-60, -30);
  const previousSpending = previous30Days.reduce(
    (sum, d) => sum + d.spending,
    0,
  );

  if (previousSpending === 0) {
    return currentSpending > 0 ? 100 : 0;
  }

  return Math.round(
    ((currentSpending - previousSpending) / previousSpending) * 100,
  );
};

export default memo(function SpendChart({ financeData }: SpendChartProps) {
  const spend = calculateSpend(financeData);
  const trend = calculateTrend(financeData);
  const chartData = generateChartData(financeData);
  // Return null if no data
  if (spend === null || chartData === null) {
    return null;
  }

  const naturalSpacing =
    (CHART_WIDTH - 16 - POINTER_LABEL_WIDTH / 2) / (chartData.length - 1 || 1);
  const effectiveSpacing = Math.min(naturalSpacing, MAX_SPACING);
  const effectiveChartWidth =
    effectiveSpacing < naturalSpacing
      ? Math.round(
          8 + effectiveSpacing * (chartData.length - 1) + POINTER_LABEL_WIDTH / 2 + 8,
        )
      : CHART_WIDTH;

  // For spending: decreased spending (negative trend) is GOOD (green)
  // Increased spending (positive trend) is BAD (red)
  const isSpendingDown = (trend ?? 0) <= 0;

  const handleUpdateBudget = () => {
    router.push({
      pathname: "/my-money/set-budget",
    });
  };

  return (
    <View style={styles.card}>
      {/* Header */}
      <Text style={styles.title}>Spend</Text>

      {/* Amount with badge */}
      <View style={styles.amountRow}>
        <Text style={styles.amount}>{formatCurrency(spend)}</Text>
        {trend !== null && (
          <View style={[styles.badge, !isSpendingDown && styles.badgeNegative]}>
            <Text
              style={[
                styles.badgeText,
                !isSpendingDown && styles.badgeTextNegative,
              ]}
            >
              {trend >= 0 ? "↑" : "↓"}
              {Math.abs(trend)}%
            </Text>
          </View>
        )}
      </View>

      {/* Date range */}
      <Text style={styles.dateRange}>{formatDateRange()}</Text>

      {/* Line Chart */}
      <View
        style={[
          styles.chartContainer,
          effectiveChartWidth < CHART_WIDTH && styles.chartContainerCentered,
        ]}
      >
        <LineChart
          data={chartData}
          parentWidth={effectiveChartWidth}
          width={effectiveChartWidth}
          height={150}
          hideRules
          hideYAxisText
          hideAxesAndRules
          hideDataPoints
          overflowTop={30}
          thickness={4}
          color={Colors.error}
          curved
          curveType={1}
          areaChart
          startFillColor={"rgba(239, 68, 68, 0.12)"}
          endFillColor={"rgba(239, 68, 68, 0.02)"}
          startOpacity={0.2}
          endOpacity={0.05}
          initialSpacing={8}
          endSpacing={POINTER_LABEL_WIDTH / 2 + 8}
          spacing={effectiveSpacing}
          isAnimated
          animationDuration={800}
          mostNegativeValue={-1}
          pointerConfig={{
            initialPointerIndex: chartData.length - 1,
            stripOverPointer: true,
            pointerStripWidth: 0,
            pointerStripColor: Colors.error,
            pointerColor: Colors.error,
            radius: 6,
            pointerComponent: () => (
              <View
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: Colors.error,
                  transform: [{ translateX: -7 }],
                }}
              />
            ),
            pointerLabelWidth: POINTER_LABEL_WIDTH,
            pointerLabelHeight: 46,
            activatePointersOnLongPress: false,
            autoAdjustPointerLabelPosition: true,
            persistPointer: true,
            pointerLabelComponent: (
              items: { value: number; date: string }[],
            ) => {
              const item = items[0];
              const value = item?.value ?? 0;
              const dateLabel = item?.date ? formatPointerDate(item.date) : "";
              return (
                <View style={styles.pointerLabel}>
                  <Text style={styles.pointerDateText}>{dateLabel}</Text>
                  <Text style={styles.pointerLabelText}>
                    {formatCurrency(value * 100)}
                  </Text>
                </View>
              );
            },
          }}
        />
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Update budget */}
      <TouchableOpacity style={styles.connectRow} onPress={handleUpdateBudget}>
        <View>
          <Text style={styles.connectTitle}>Update your budget</Text>
          <Text style={styles.connectSubtitle}>
            Set your monthly savings goal
          </Text>
        </View>
        <Icons.ChevronRight width={24} height={24} />
      </TouchableOpacity>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    paddingVertical: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
    marginBottom: 8,
  },
  amountRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 4,
  },
  amount: {
    fontSize: 36,
    fontFamily: FontFamilies.InterTight.Bold,
    color: "#161D14",
  },
  badge: {
    backgroundColor: "#ECFDF5",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeNegative: {
    backgroundColor: "#FEF2F2",
  },
  badgeText: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
  },
  badgeTextNegative: {
    color: "#EF4444",
  },
  dateRange: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    textAlign: "center",
    marginBottom: 16,
  },
  chartContainer: {
    marginBottom: 16,
    paddingTop: 16,
  },
  chartContainerCentered: {
    alignItems: "center",
  },
  pointerLabel: {
    backgroundColor: "#EF4444",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    alignItems: "center",
  },
  pointerDateText: {
    fontSize: 11,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(255, 255, 255, 0.8)",
  },
  pointerLabelText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#FFFFFF",
  },
  divider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginBottom: 16,
  },
  connectRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  connectTitle: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    marginBottom: 2,
  },
  connectSubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
  },
});
